import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { AssetFolder, ProjectFolder } from '@vidmakr/bindings/types';
import { ChevronRight, Folder, Library, Lock } from 'lucide-react';
import { subtreeIds } from '@vidmakr/shared';
import { PROJECT_DRAG, moveProjectItems, placeUrl, useSpaces } from '@/lib/spaces';
import { MEDIA_DRAG, assetsUrl, moveMediaItems, type MediaDest } from '@/lib/library';
import { useStoredState } from '@/lib/browse';
import { toast } from '@/lib/toast';
import { cx } from './ui';

/**
 * Folder trees in the sidebar: spaces and their project folders under
 * Projects, the shared and space media libraries and their folders under
 * Asset library. Entries are links and drop targets (projects and folders
 * dragged from the project browser, files and folders from the asset
 * library). Which entries are expanded is remembered per browser; the way
 * to the current page is always expanded and highlighted.
 */

interface Node {
  /** Unique in the tree (also the expand/collapse key). */
  id: string;
  label: string;
  to: string;
  icon: ReactNode;
  restricted?: boolean;
  active: boolean;
  children: Node[];
  /** Drag type it accepts and what dropping keys on it does. */
  drop?: { mime: string; onDrop: (keys: string[]) => void };
  attrs?: Record<string, string>;
}

const OPEN_KEY = 'vm:nav-open';
const isStringArray = (v: unknown) => Array.isArray(v) && v.every((x) => typeof x === 'string');

function useOpenNodes() {
  const [open, setOpen] = useStoredState<string[]>(OPEN_KEY, [], isStringArray);
  const set = useMemo(() => new Set(open), [open]);
  const toggle = (id: string, now: boolean) => setOpen((o) => (now ? o.filter((x) => x !== id) : [...o.filter((x) => x !== id), id]));
  /** Sets several entries at once (`!id` entries record "closed" where the default is open). */
  const put = (on: string[], off: string[]) => setOpen((o) => [...o.filter((x) => !on.includes(x) && !off.includes(x)), ...on]);
  return { isOpen: (id: string) => set.has(id), toggle, put };
}

/** Builds folder nodes below `parentId` (folders whose parent can't be seen count as top level). */
function folderNodes<F extends { id: string; parentId: string; name: string; restricted: boolean }>(list: readonly F[], parentId: string, make: (f: F, children: Node[]) => Node, depth = 0): Node[] {
  const ids = new Set(list.map((f) => f.id));
  return list
    .filter((f) => (parentId ? f.parentId === parentId : !f.parentId || !ids.has(f.parentId)))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((f) => make(f, depth < 32 ? folderNodes(list, f.id, make, depth + 1) : []));
}

function Tree({ nodes, depth, open, dropOn, setDropOn, reveal }: { nodes: Node[]; depth: number; open: ReturnType<typeof useOpenNodes>; dropOn: string; setDropOn: (id: string) => void; reveal: Set<string> }) {
  return (
    <>
      {nodes.map((n) => {
        const expanded = open.isOpen(n.id) || reveal.has(n.id);
        return (
          <div key={n.id}>
            <div className="flex items-center" style={{ paddingLeft: depth * 12 }}>
              {n.children.length ? (
                <button
                  type="button"
                  aria-label={expanded ? `Collapse ${n.label}` : `Expand ${n.label}`}
                  aria-expanded={expanded}
                  className="flex h-6 w-5 shrink-0 items-center justify-center rounded text-faint hover:text-fg"
                  onClick={() => open.toggle(n.id, expanded)}
                  data-nav-toggle={n.id}
                >
                  <ChevronRight size={12} className={cx('transition-transform', expanded && 'rotate-90')} />
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              <Link
                to={n.to}
                aria-current={n.active ? 'page' : undefined}
                className={cx(
                  'flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-[13px]',
                  dropOn === n.id ? 'bg-accent/20 text-fg ring-1 ring-accent' : n.active ? 'bg-panel-3 text-fg' : 'text-muted hover:bg-panel-2 hover:text-fg',
                )}
                onDragOver={(e) => {
                  if (!n.drop || !e.dataTransfer.types.includes(n.drop.mime)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dropOn !== n.id) setDropOn(n.id);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node)) setDropOn('');
                }}
                onDrop={(e) => {
                  setDropOn('');
                  if (!n.drop || !e.dataTransfer.types.includes(n.drop.mime)) return;
                  const keys = JSON.parse(e.dataTransfer.getData(n.drop.mime) || '[]') as string[];
                  if (!keys.length) return;
                  e.preventDefault();
                  n.drop.onDrop(keys);
                }}
                {...n.attrs}
              >
                {n.icon}
                <span className="truncate">{n.label}</span>
                {n.restricted ? <Lock size={11} className="ml-auto shrink-0 text-faint" /> : null}
              </Link>
            </div>
            {expanded && n.children.length ? <Tree nodes={n.children} depth={depth + 1} open={open} dropOn={dropOn} setDropOn={setDropOn} reveal={reveal} /> : null}
          </div>
        );
      })}
    </>
  );
}

/** Ids of the nodes above an active node (so the current place is visible). */
function revealed(nodes: Node[]): Set<string> {
  const out = new Set<string>();
  const walk = (n: Node): boolean => {
    let below = false;
    for (const c of n.children) if (walk(c)) below = true;
    if (below) out.add(n.id);
    return below || n.active;
  };
  nodes.forEach(walk);
  return out;
}

const folderIcon = <Folder size={13} className="shrink-0 text-faint" />;
const dot = (color: string) => <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />;

/** Spaces and their folders, under Projects. */
export function ProjectsTree({ orgId, showGeneral }: { orgId: string; showGeneral: boolean }) {
  const sp = useSpaces(orgId);
  const { pathname } = useLocation();
  const open = useOpenNodes();
  const [dropOn, setDropOn] = useState('');
  const m = /^\/orgs\/[^/]+\/s\/([^/]+)(?:\/f\/([^/]+))?/.exec(pathname);
  const here = m ? { spaceId: m[1] === 'general' ? '' : m[1], folderId: m[2] ?? '' } : null;

  const drop = (spaceId: string, folderId: string): Node['drop'] => ({
    mime: PROJECT_DRAG,
    onDrop: (keys) => {
      if (folderId && keys.some((k) => k.startsWith('f:') && subtreeIds(sp.folders, k.slice(2)).has(folderId))) return void toast.error('A folder can’t move into itself');
      void moveProjectItems(keys, { spaceId, folderId });
    },
  });
  const folderNode = (f: ProjectFolder, children: Node[]): Node => ({
    id: `pf:${f.id}`,
    label: f.name,
    to: placeUrl(orgId, f.spaceId, f.id),
    icon: folderIcon,
    restricted: f.restricted,
    active: !!here && here.folderId === f.id,
    children,
    drop: drop(f.spaceId, f.id),
    attrs: { 'data-nav-folder': f.id },
  });
  const spaceNode = (id: string, name: string, color: string, restricted: boolean): Node => ({
    id: `space:${id}`,
    label: name,
    to: placeUrl(orgId, id),
    icon: dot(color),
    restricted,
    active: !!here && here.spaceId === id && !here.folderId,
    children: folderNodes(
      sp.folders.filter((f) => f.spaceId === id),
      '',
      folderNode,
    ),
    drop: drop(id, ''),
    attrs: { 'data-space-nav-item': id || 'general' },
  });
  const nodes = [
    ...(showGeneral && (sp.spaces.length || sp.folders.some((f) => !f.spaceId)) ? [spaceNode('', 'General', '#64748b', false)] : []),
    ...sp.spaces.map((s) => spaceNode(s.id, s.name, s.color, s.restricted)),
    // Folders shared with someone who can't see their space (guests).
    ...folderNodes(
      sp.folders.filter((f) => f.spaceId && !sp.spaceById.has(f.spaceId)),
      '',
      folderNode,
    ),
  ];
  if (!nodes.length) return null;
  return (
    <div className="mb-1 ml-3 flex flex-col gap-0.5 border-l border-line pl-1" data-space-nav>
      <Tree nodes={nodes} depth={0} open={open} dropOn={dropOn} setDropOn={setDropOn} reveal={revealed(nodes)} />
    </div>
  );
}

/** The shared library, each space's library, and their folders, under Asset library. */
export function AssetsTree({ orgId }: { orgId: string }) {
  const sp = useSpaces(orgId);
  const [folders] = useTable(tables.assetFolder.where((f) => f.orgId.eq(orgId)));
  const { pathname, search } = useLocation();
  const open = useOpenNodes();
  const [dropOn, setDropOn] = useState('');
  const onAssets = pathname === `/orgs/${orgId}/assets`;
  const q = new URLSearchParams(search);
  const lib = q.get('lib') || 'all';
  const folderId = q.get('folder') ?? '';
  const libFolders = folders.filter((f) => !f.projectId);

  const drop = (dest: MediaDest): Node['drop'] => ({ mime: MEDIA_DRAG, onDrop: (keys) => void moveMediaItems(keys, dest, folders) });
  const folderNode = (f: AssetFolder, children: Node[]): Node => ({
    id: `af:${f.id}`,
    label: f.name,
    to: assetsUrl(orgId, f.spaceId ? `space:${f.spaceId}` : 'library', f.id),
    icon: folderIcon,
    restricted: f.restricted,
    active: onAssets && folderId === f.id,
    children,
    drop: drop({ folderId: f.id, projectId: f.projectId, spaceId: f.spaceId }),
    attrs: { 'data-nav-asset-folder': f.id },
  });
  const libraryNode = (spaceId: string, name: string, icon: ReactNode, restricted = false): Node => {
    const id = spaceId ? `space:${spaceId}` : 'library';
    return {
      id: `lib:${id}`,
      label: name,
      to: assetsUrl(orgId, id),
      icon,
      restricted,
      active: onAssets && lib === id && !folderId,
      children: folderNodes(
        libFolders.filter((f) => f.spaceId === spaceId),
        '',
        folderNode,
      ),
      drop: drop({ folderId: '', projectId: '', spaceId }),
      attrs: { 'data-nav-library': id },
    };
  };
  const nodes = [
    // Guests only see what was shared with them, not the organization's shared library.
    ...(sp.role !== 'guest' ? [libraryNode('', 'Shared library', <Library size={13} className="shrink-0 text-faint" />)] : []),
    ...sp.spaces.map((s) => libraryNode(s.id, s.name, dot(s.color), s.restricted)),
  ];
  // Collapsed under the Asset library entry until opened; open while browsing the library unless closed there.
  const expanded = open.isOpen('assets') || (onAssets && !open.isOpen('!assets'));
  return (
    <>
      <button
        type="button"
        aria-label={expanded ? 'Collapse libraries' : 'Expand libraries'}
        aria-expanded={expanded}
        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded text-faint hover:text-fg"
        onClick={() => (expanded ? open.put(['!assets'], ['assets']) : open.put(['assets'], ['!assets']))}
        data-nav-toggle="assets"
      >
        <ChevronRight size={13} className={cx('transition-transform', expanded && 'rotate-90')} />
      </button>
      {expanded ? (
        <div className="mb-1 ml-3 flex flex-col gap-0.5 border-l border-line pl-1" data-library-nav>
          <Tree nodes={nodes} depth={0} open={open} dropOn={dropOn} setDropOn={setDropOn} reveal={revealed(nodes)} />
        </div>
      ) : null}
    </>
  );
}
